// Libraries
import axios, { AxiosError, AxiosPromise } from "axios";

// Types
import {
  StripboardTableBanner,
  StripboardTableDayBreak,
  StripboardTableScene,
} from "components/dashboard/body/projects/panels/schedule/types";
import { FeatureStatus } from "store/types/project";

// Config
import {
  FEATURE_STATUS_CANCELLED,
  FEATURE_STATUS_COMPLETED,
  FEATURE_STATUS_FAILED,
  FEATURE_STATUS_PENDING,
  FEATURE_STATUS_RERUNNING,
} from "config";

export interface OriginalObject {
  [key: string]: any;
}

export const filterProperties = (obj: OriginalObject, filteredObj: OriginalObject) => {
  for (const key in obj) {
    if (obj[key] !== null) {
      if (typeof obj[key] === "object" && !Array.isArray(obj[key])) {
        filteredObj[key] = {};
        filterProperties(obj[key], filteredObj[key]);
      } else {
        filteredObj[key] = obj[key];
      }
    }
  }
  return filteredObj;
};

export const reorderRows = (list: any[], startIndex: number, endIndex: number) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
};

export type ProjectFeatureStatusItem = {
  featureStatusId: number;
  feature: string;
  status: string;
  metadata?: unknown;
};

export const mapProjectFeatureStatusesToFeatureStatus = (
  featureStatuses: ProjectFeatureStatusItem[] | undefined,
  projectId: string | number
): FeatureStatus[] => {
  if (!featureStatuses?.length) return [];
  return featureStatuses.map((fs) => ({
    feature_status_id: fs.featureStatusId,
    project_id: String(projectId ?? ""),
    feature: fs.feature,
    status: fs.status,
    created_at: "",
    updated_at: "",
    metadata: fs.metadata,
  }));
};

export const getProjectFeatureStatus = (
  project_features: FeatureStatus[] | undefined,
  feature: string
) => {
  if (!project_features || project_features.length === 0) return;

  const selectedFeature = project_features?.filter((feati) => feati.feature === feature)[0];

  return selectedFeature;
};

export const checkProjectFeatureStatus = (
  project_features: FeatureStatus[] | undefined,
  feature: string
) => {
  if (!project_features || project_features.length === 0) return true;

  const entry = project_features.filter((feati) => feati.feature === feature)[0];
  if (!entry) return false;

  // A cancelled run ends like a failed one: callers show existing rows or their empty state.
  const feature_status =
    entry.status === FEATURE_STATUS_COMPLETED ||
    entry.status === FEATURE_STATUS_FAILED ||
    entry.status === FEATURE_STATUS_CANCELLED ||
    entry.status === FEATURE_STATUS_PENDING ||
    entry.status === FEATURE_STATUS_RERUNNING;
  return feature_status;
};

export const checkIfAnyRunningStatus = (project_features: FeatureStatus[] | undefined) => {
  if (!project_features || project_features.length === 0) return true;

  const pending_status = !!project_features?.find(
    (stat) => stat.status === FEATURE_STATUS_PENDING || stat.status === FEATURE_STATUS_RERUNNING
  );
  return pending_status;
};

export const formatNumberWithCommas = (number: number, emptyIfNoValue: boolean = false) => {
  if (!number && emptyIfNoValue) return "";

  return (
    number.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || ""
  );
};

export const areDatesEqual = (date1: Date, date2: Date) => {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
};

export const instanceOfStripboardTableScene = (object: any): object is StripboardTableScene => {
  return object && "scene" in object;
};

export const instanceOfStripboardTableDayBreak = (
  object: any
): object is StripboardTableDayBreak => {
  return object && "daybreak_id" in object;
};

export const instanceOfStripboardTableHeader = (object: any): object => {
  return object && "header_name" in object;
};

export const instanceOfStripboardTableBanner = (object: any): object is StripboardTableBanner => {
  return object && "stripoard_banner_id" in object;
};

export const isLockedDaybreak = (object: any): object is StripboardTableDayBreak => {
  return object && "daybreak_id" in object && object.locked;
};

export const isPunctuation = (char: string) => {
  return /[.,/#!$%^&*;:{}=\-_`~()]/.test(char);
};

export const getSelectionRange = (selection: Selection) => {
  return selection.rangeCount > 0 ? selection.getRangeAt(0) : null;
};

export const swapSelectionRange = (selection: Selection, newRange: Range) => {
  selection.removeAllRanges();
  selection.addRange(newRange);
};

export const getWindowCursorPosition = (event: React.MouseEvent) => {
  const { clientX, clientY } = event;
  const scrollX = window.scrollX || window.pageXOffset;
  const scrollY = window.scrollY || window.pageYOffset;
  return { x: clientX + scrollX, y: clientY + scrollY };
};

export const moveInArray = (arr: any, fromIndex: number, toIndex: number) => {
  const element = arr[fromIndex];
  arr.splice(fromIndex, 1);
  arr.splice(toIndex, 0, element);
};

export const getValueByPath = (obj: any, path: string) => {
  const keys = path.split(".");
  return keys.reduce((accumulator, key) => {
    return accumulator && accumulator[key] !== undefined ? accumulator[key] : undefined;
  }, obj);
};

export const removeDuplicates = (array: any[], propertyName: string) => {
  const seen = new Set();
  return array.filter((item) => {
    const duplicate = seen.has(item[propertyName]);
    seen.add(item[propertyName]);
    return !duplicate;
  });
};

export const sanitizeFileName = (name?: string) => {
  if (!name) return name;
  let sanitized = name.replace(/[<>:"\/\\|?*]+/g, "_"); // Replaces invalid characters with underscore
  sanitized = sanitized.replace(/\.{2,}/g, "_"); // Replace multiple periods with a single period
  sanitized = sanitized.replace(/^\.+|\.+$/g, ""); // Remove periods at the beginning or end of the filename
  return sanitized;
};

export interface FetchResponse<T> {
  status_code: number;
  message: string;
  data: T;
}

/** Standard API error response contract (backend and frontend). */
export interface ApiErrorResponse {
  status_code: number;
  message: string;
  code?: string;
  error?: string;
  data?: Record<string, unknown>;
  retry_after?: string | number;
}

/**
 * Wire shape from backend. API sends only `code` (see docs/API_ERROR_RESPONSE.md).
 * We include error_code in the type for backward compatibility when parsing legacy responses.
 */
type TApiError = {
  status_code?: number;
  statusCode?: number;
  message?: string;
  error?: string;
  code?: string;
  error_code?: string;
  data?: Record<string, unknown>;
  retry_after?: string | number;
  [key: string]: unknown;
};

export class ServerError implements ApiErrorResponse {
  status_code: number;
  message: string;
  code?: string;
  error?: string;
  data?: Record<string, unknown>;
  retry_after?: string | number;

  constructor(
    status_code: number,
    message: string,
    error?: string,
    code?: string,
    data?: Record<string, unknown>,
    retry_after?: string | number
  ) {
    this.status_code = status_code;
    this.message = message;
    this.error = error;
    this.code = code;
    this.data = data;
    this.retry_after = retry_after;
  }

  static fromAxiosError(error: AxiosError<TApiError>): ServerError {
    const responseData = error.response?.data as TApiError | undefined;
    const statusCode = (responseData?.status_code ??
      responseData?.statusCode ??
      error.response?.status ??
      500) as number;
    const rawMessage = responseData?.message ?? error.message;
    const message = Array.isArray(rawMessage)
      ? (rawMessage[0] ?? rawMessage.join(", "))
      : typeof rawMessage === "string"
        ? rawMessage
        : String(rawMessage);

    if (!responseData) {
      return new ServerError(statusCode, message, undefined, undefined);
    }

    const code = responseData.code ?? responseData.error_code;
    const data = responseData.data as Record<string, unknown> | undefined;
    const retry_after = responseData.retry_after;

    const serverError = new ServerError(
      statusCode,
      message,
      responseData.error,
      code,
      data,
      retry_after
    );

    return serverError;
  }
}

export const handleAxiosRequest = async <T>(request: AxiosPromise<T>) => {
  try {
    return (await request).data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw ServerError.fromAxiosError(error);
    }
    if (error instanceof Error) {
      throw error.message;
    }
    throw error;
  }
};

export { type TApiError };

/**
 * Normalizes an ID to a string format
 * Useful for consistent handling of IDs across the application
 * @param id The ID to normalize (can be string or number)
 * @returns The ID as a string
 */
export const normalizeId = (id: string | number): string => String(id);

/**
 * Helper function to normalize string values - convert empty strings to null
 * This is useful for API requests to ensure empty form fields are properly handled
 * @param obj The object to normalize
 * @returns The normalized object
 */
export const normalizeEmptyStringsToNull = <T extends Record<string, any>>(obj: T): T => {
  if (!obj) return obj;

  return Object.entries(obj).reduce((result: any, [key, value]) => {
    // Handle nested objects recursively
    if (value && typeof value === "object" && !Array.isArray(value)) {
      result[key] = normalizeEmptyStringsToNull(value);
    }
    // Convert empty strings to null
    else if (typeof value === "string" && value.trim() === "") {
      result[key] = null;
    }
    // Keep other values as is
    else {
      result[key] = value;
    }
    return result;
  }, {});
};
